import { Axis, Direction, FrenzyLine, FrenzyPolygon, Point } from "../Model";
import { clampAboveZero, describePoint, minMax } from "../Utils";
import { describeLine, describeLines, findLinesThatStartOrEndAt, isOnVerticalLine, lengthOfAllLines, reverse, rightmostPointOfLine, subtract, topmostPointOfLine, topmostYOfLine, vectorizeIntoHorizontalLines, vectorizeIntoVerticalLines } from "./Line";
import { areSame, isPointOnLine, translateX, translateXY, translateY } from "./Point";
import { length } from './Line';
import { FrenzyPointVisitor, isFailure, visitEachPointOnLine } from "../model/LinePointVisitor";
import { isOnLine } from "../GameEngine";

export function describePolygon (poly: FrenzyPolygon | undefined){
  if (!poly) {
    return "None"
  }
  const { polygonLines, area, innerArea } = poly;
  return `Polygon: ${polygonLines.length} lines: ${describeLines(polygonLines)}, area: ${area}, innerArea: ${innerArea}`;
};

export function describePolygons (polys: FrenzyPolygon[]) {
  return polys.reduce((acc, p) => {
    return acc + `${describePolygon(p)},\n`;
  }, '');
};


export function boundingRectangleForPolygon (polygonLines: FrenzyLine[], maxX:number = 9999, maxY:number = 9999) {
  const top = polygonLines.reduce((acc, l) => {
    const smallestY = minMax(l.start[1], l.end[1])[0];
    return minMax(acc, smallestY)[0];
  }, maxY);

  const left = polygonLines.reduce((acc, l) => {
    const smallestX = minMax(l.start[0], l.end[0])[0];
    return minMax(acc, smallestX)[0];
  }, maxX);

  const right = polygonLines.reduce((acc, l) => {
    const largestX = minMax(l.start[0], l.end[0])[1];
    return minMax(acc, largestX)[1];
  }, 0);

  const bottom = polygonLines.reduce((acc, l) => {
    const largestY = minMax(l.start[1], l.end[1])[1];
    return minMax(acc, largestY)[1];
  }, 0);

  return {
    top,
    right,
    bottom,
    left
  };
};

export const topmostPointForPolygon = (polygon: FrenzyPolygon):number => {
  const bounds = boundingRectangleForPolygon(polygon.polygonLines);
  return bounds.top;
};

export function topmostPointForPolygons(polygons: Array<FrenzyPolygon>):number {
  return polygons.reduce((acc, poly) => {
    const smallestY = topmostPointForPolygon(poly);
    return minMax(acc, smallestY)[0];
  }, 9999);
}


export const leftmostPointForPolygon = (polygon: FrenzyPolygon):number => {
  const bounds = boundingRectangleForPolygon(polygon.polygonLines);
  return bounds.left;
};
export const rightmostPointForPolygon = (polygon: FrenzyPolygon):number => {
  const bounds = boundingRectangleForPolygon(polygon.polygonLines);
  return bounds.right;
};

export const mostBottomRightActualPointForPolygon = (polygon: FrenzyPolygon):Point => {
  // To replicate the game, we find the bottom-most point, and then 
  // find the rightmost point along that. i.e. Y has priority over X.

  const bounds = boundingRectangleForPolygon(polygon.polygonLines);

  const bottomLines = polygon.polygonLines.filter(l => l.direction.axis === Axis.HORIZONTAL && minMax(l.start[1], l.end[1])[1] === bounds.bottom);
  // console.log(`bottomLines: ${describeLines(bottomLines)}`);

  const rightMost = bottomLines.reduce((acc, l) => {
    const largestX = minMax(l.start[0], l.end[0])[1];
    return minMax(acc, largestX)[1];
  }, 0);

  return [rightMost, bounds.bottom];
};


/** Iterate around the polygon in an anticlockwise direction starting from the bottom-right,calling
 * the visitorFn until it returns something truthy.
 * If we get back to the start point, return an Error
 */
export function visitEachPointOnPolygonLines<T>(polygon: FrenzyPolygon, visitorFn: FrenzyPointVisitor<T>): T | Error {
  const { polygonLines } = polygon;
  const bottomRight = mostBottomRightActualPointForPolygon(polygon);
  const verticalLinesOriginatingAtBottomRight = findLinesThatStartOrEndAt(polygonLines, bottomRight, Axis.VERTICAL);
  if (verticalLinesOriginatingAtBottomRight.length === 0) {
    const msg = `Expected to be able to traverse vertically from ${describePoint(bottomRight)}`;
    return new Error(msg);
  }
  const vLine = verticalLinesOriginatingAtBottomRight[0];
  const result = visitEachPointOnLine(vLine, bottomRight, visitorFn);
  if (isFailure(result)) {
    // If we've reached the end, that's bad
    if (result.lineEndPoint === bottomRight) {
      return new Error("not found")
    } else {
      // No matter, move onto the next line and continue.
      const newPolygonLines = subtract(vLine, polygonLines);
      return recurseAlongPolygonLines(newPolygonLines, result.lineEndPoint, visitorFn)
      
    }  
  }
  return result.result;
}

function recurseAlongPolygonLines<T>(polygonLines: Array<FrenzyLine>, startPoint: Point, visitorFn: FrenzyPointVisitor<T>): T | Error {
  const nextLines = findLinesThatStartOrEndAt(polygonLines, startPoint); 
  if (nextLines.length !== 1) {
    const msg = `Unable to traverse from ${describePoint(startPoint)}`;
    return new Error(msg);
  }
  const nextLine = nextLines[0];
  const result = visitEachPointOnLine(nextLine, startPoint, visitorFn);
  if (isFailure(result)) {
    // If we've reached the end, that's bad
    if (polygonLines.length === 1) {
      return new Error("not found - edges exhausted")
    } else {
      // No matter, move onto the next line and continue.
      const newPolygonLines = subtract(nextLine, polygonLines);
      return recurseAlongPolygonLines(newPolygonLines, result.lineEndPoint, visitorFn)
    }  
  }
  return result.result;
}



export const mostBottomRightInteriorPointForPolygon = (polygon: FrenzyPolygon):Point => {
  const { polygonLines } = polygon;
  const bottomRight = mostBottomRightActualPointForPolygon(polygon);
  let startPoint: Point = translateXY(bottomRight, -1, -1);
  if (!isPointOnLine(startPoint, polygonLines)) {
    return startPoint;
  } else {

    const searchResult = visitEachPointOnPolygonLines(polygon, (p: Point) => {
      const testPoint = translateXY(p, -1, -1);
      if (testPoint[0] > 0 && testPoint[1] > 0) {
        // console.log(`checking ${describePoint(testPoint)} -> on line: ${isPointOnLine(testPoint, polygonLines)}`)
        if (!isPointOnLine(testPoint, polygonLines)) {
          startPoint = testPoint;
          return true
        }
      }
      return false;
    })

    if (searchResult instanceof Error) {
      console.error(`Found no solution at ${describePoint(bottomRight)}, hit limit`, searchResult);
    }

    if (searchResult) {
      // console.log(`Found what we wanted at ${startPoint}`)
      return startPoint;
    }

  

    return bottomRight;
  }
};

// http://mathopenref.com/coordpolygonarea.html
export const areaOfPolygon = (lines: FrenzyLine[]) => {
  if (lines.length === 1) {
    return length(lines[0])
  }
  if (lines.length < 4) {
    throw new Error('Not a polygon with square edges');
  }
  const sum = lines.reduce((acc, line) => {
    const term = (line.start[0] * line.end[1]) - (line.end[0] * line.start[1]);
    return acc + term;
  }, 0);

  return Math.abs(sum) / 2;
};


export function linesToPolygon(polygonLines: FrenzyLine[]): FrenzyPolygon {
  // Lines have a finite width. so the inner area tries to replicate how
  // many pixels would need to be coloured in.
  return {
    polygonLines,
    area: areaOfPolygon(polygonLines) + lengthOfAllLines(polygonLines),
    innerArea: clampAboveZero(
      areaOfPolygon(polygonLines) - lengthOfAllLines(polygonLines)
    ),
  };
}

export function isPointAnywhereOnPolygonLines(point: Point, polys: FrenzyPolygon[]): boolean {
  return polys.some((poly) => {
    const { polygonLines } = poly;
    return isPointOnLine(point, polygonLines)
  });
}


// Count vertical intersections as per
// http://alienryderflex.com/polygon/
export const isInsideComplexPolygon = (x: number, y: number, lines: FrenzyLine[]) => {
	// Simple checks to avoid the slightly flawed behaviour on boundary lines:
  const bounds = boundingRectangleForPolygon(lines);
  if (x > bounds.right || x < bounds.left) {
    return false;
  }
  if (y > bounds.bottom || y < bounds.top) {
    return false;
  }
  
  
  const verticalLinesThatAreLeftOfTarget = lines.filter(line => line.direction.axis === Axis.VERTICAL).filter(line => {
		if (line.start[0] <= x) {
			return true;	
		}
		return false;	
	});	

  // console.log(`verrs: ${describeLines(verticalLinesThatAreLeftOfTarget)}`)
	
	const nodesThatAreLeftOfTarget = verticalLinesThatAreLeftOfTarget.filter(line => {
    // console.log(`isOn: ${y}, ${describeLine(line)}: ${isOnVerticalLine(y, line)}`)
		return isOnVerticalLine(y, line);
	});

	return (nodesThatAreLeftOfTarget.length % 2) === 1;
};

/** is it within the mathematical polygon area or on one of the bounding lines? */
export const isInsideComplexPolygonInclusive = (x: number, y: number, lines: FrenzyLine[]) => {
  return isInsideComplexPolygon(x, y, lines) || isOnLine(x, y, lines);
};

export const isInsideComplexPolygons = (x: number, y: number, polys: FrenzyPolygon[]) => {
	return polys.some(poly => {
		return isInsideComplexPolygon(x, y, poly.polygonLines);
	}); 
};

/** Turn a single line into zero or more shorter lines that are within the polygon */
export function trimToFit(polygonLines: FrenzyLine[], line: FrenzyLine): Array<FrenzyLine> {
  const points : Array<Point> = [];
  
  visitEachPointOnLine(line, line.start, (p) => {
    if (isInsideComplexPolygonInclusive(p[0], p[1], polygonLines)) {
      points.push(p);
    }
  })

  switch (line.direction) {
    case Direction.RIGHT:
      return vectorizeIntoHorizontalLines(points);
    case Direction.LEFT:
      return vectorizeIntoHorizontalLines(points).map(reverse);
    case Direction.DOWN:
      return vectorizeIntoVerticalLines(points);
    case Direction.UP:
    default:
      return vectorizeIntoVerticalLines(points).map(reverse);
  }
}

export function truncatePolygonAt(polygonLines: FrenzyLine[], drainAnimationRow: number):FrenzyLine[] {
  const result = polygonLines.flatMap(l => {
    const { direction, start, end } = l;

    // console.log(`processing line starting at ${describePoint(start)}`)
    const newLine = {
      direction,
      start: [...start] as Point,
      end: [...end] as Point,
    };

    if (newLine.start[1] < drainAnimationRow) {
      // console.log(`abbreviating start ${describeLine(newLine)} to ${drainAnimationRow}`)
      newLine.start[1] = drainAnimationRow;
    }

    if (newLine.end[1] < drainAnimationRow) {
      // console.log(`abbreviating end ${describeLine(newLine)} to ${drainAnimationRow}`)
      newLine.end[1] = drainAnimationRow;
    }
    
    if (areSame(newLine.start, newLine.end)) {
      // console.log(`dropping zero-length ${describeLine(newLine)}`)
      return [];
    }


    // if (!isInsideComplexPolygonInclusive(newLine.start[0], newLine.start[1], polygonLines) ||
    // !isInsideComplexPolygonInclusive(newLine.end[0], newLine.end[1], polygonLines)) {
    //   const trimResult 
    //   console.log(`trimming ${describeLine(newLine)} - outside polygon`)
    //   return 
    // }

    const trimResult = trimToFit(polygonLines, newLine);
    // console.log(`final result on ${describeLine(newLine)} - was ${describeLines(trimResult)}`)

    return trimResult.filter((l) => !areSame(l.start, l.end));    
  }); 
  // There can be many contradictory lines along the cut row - optimize them
  return optimizeAlong(result, drainAnimationRow);
}

function optimizeAlong(lines: Array<FrenzyLine>, drainAnimationRow: number): Array<FrenzyLine> {
  return lines; // TODO
}
